import React, { useState, useEffect } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Mycelium from "@/components/icons/Mycelium";
import { Menu, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

const Navbar = () => {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Preserve current query parameters for navigation links
  const currentParams = new URLSearchParams();
  searchParams.forEach((value, key) => {
    currentParams.append(key, value);
  });
  
  // Only keep page parameter for businesses page
  const businessesParams = searchParams.has("page") ? `?page=${searchParams.get("page")}` : "";

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Close menu when resizing from mobile to desktop
  useEffect(() => {
    if (!isMobile && isMenuOpen) {
      setIsMenuOpen(false);
    }
  }, [isMobile, isMenuOpen]);

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Mycelium className="h-6 w-6 text-eco-500" strokeWidth={1.5} />
          <span className="font-semibold text-lg">Ourcelium</span>
        </Link>
        
        {/* Mobile navigation */}
        {isMobile ? (
          <>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleMenu}
              className="md:hidden"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>

            {/* Mobile menu overlay with solid background */}
            {isMenuOpen && (
              <div 
                className="fixed inset-0 top-16 z-[100] bg-white p-4 shadow-lg"
              >
                <nav className="flex flex-col items-start gap-4 p-4 bg-white">
                  <Link 
                    to="/" 
                    className="text-base font-medium text-foreground hover:text-eco-500 transition-colors w-full py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Home
                  </Link>
                  <Link 
                    to={`/businesses${businessesParams}`}
                    className="text-base font-medium text-foreground hover:text-eco-500 transition-colors w-full py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Browse
                  </Link>
                  <Link 
                    to="/events" 
                    className="text-base font-medium text-foreground hover:text-eco-500 transition-colors w-full py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Events
                  </Link>
                  <Link 
                    to="/about" 
                    className="text-base font-medium text-foreground hover:text-eco-500 transition-colors w-full py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    About
                  </Link>
                  <Link 
                    to="/submit"
                    className="w-full"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Button className="w-full bg-eco-500 hover:bg-eco-600 text-white mt-2">
                      Add a Listing
                    </Button>
                  </Link>
                </nav>
              </div>
            )}
          </>
        ) : (
          /* Desktop navigation */
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-foreground hover:text-eco-500 transition-colors">
              Home
            </Link>
            <Link to={`/businesses${businessesParams}`} className="text-sm font-medium text-foreground hover:text-eco-500 transition-colors">
              Browse
            </Link>
            <Link to="/events" className="text-sm font-medium text-foreground hover:text-eco-500 transition-colors">
              Events
            </Link>
            <Link to="/about" className="text-sm font-medium text-foreground hover:text-eco-500 transition-colors">
              About
            </Link>
            <Link to="/submit">
              <Button variant="outline" className="border-eco-500 text-eco-500 hover:bg-eco-500 hover:text-white">
                Add a Listing
              </Button>
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Navbar;
